/*======================================================================================================================
  * Bluebell: src/app/state/account/account.effects.ts
  * Last Edited: 05/09/2025
  * Copyright 2025 Bramble CFD LTD
  * The contents of this file are NOT for redistribution
  * See AUTHORS for list of developers on this project
 =====================================================================================================================*/
import { Injectable, inject } from '@angular/core';
import { GridOverlayService } from '@app/components/grid-overlay/grid-overlay.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { EffectHandlerService } from '@services/effect-handler.service';
import {
  deleteUpdates,
  loadAccountSettings,
  loadAccountSettingsFailure,
  loadAccountSettingsSuccess,
  loadUser,
  loadUserFailure,
  loadUserSettings,
  loadUserSettingsFailure,
  loadUserSettingsSuccess,
  loadUserSuccess,
  checkForUpdates,
  updateAccountSettings,
  updateAccountSettingsFailure,
  updateAccountSettingsSuccess,
  updateSelectedAccount,
  updateUserAccountSettings,
  updateUserAccountSettingsFailure,
  updateUserAccountSettingsSuccess,
  updateUserSettings,
  updateUserSettingsFailure,
  updateUserSettingsSuccess,
  checkForNotifications,
  removeTaskNotification,
  removeAllTaskNotifications,
  getAccountBalance,
  getAccountBalanceFailure,
  getAccountBalanceSuccess,
  createDefaultGridOverlay,
  createGridOverlay,
  createGridOverlaySuccess,
  createGridOverlayFailure,
  updateGridOverlay,
  updateGridOverlaySuccess,
  updateGridOverlayFailure,
  deleteGridOverlay,
  deleteGridOverlaySuccess,
  deleteGridOverlayFailure,
  createMapParameters,
  createMapParametersSuccess,
  createMapParametersFailure,
  deleteMapParameter,
  deleteMapParameterSuccess,
  deleteMapParameterFailure,
  updateMapParameter,
  updateMapParameterSuccess,
  updateMapParameterFailure,
} from '@state/account/account.actions';
import {
  Account,
  AccountMapParameter,
  AccountSettings,
  CustomOverlay,
  User,
  UserAccountSettings,
  UserSettings,
} from '@state/account/account.interfaces';
import { selectAccount } from '@state/account/account.selectors';
import { ApiService } from '@state/api.service';
import { getAccessTokenSuccess, refreshAccessTokenSuccess } from '@state/authentication/authentication.actions';
import { selectGridOverlay } from '@state/postpro/postpro.actions';
import { ProgrammeActions } from '@state/programmes/programme.actions';
import { selectCurrentProgrammeId } from '@state/programmes/programme.selectors';
import { selectCurrentProjectId } from '@state/projects/project.selectors';
import { mergeMap } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { ProjectActions } from '@state/projects/project.actions';


@Injectable()
export class AccountEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private api = inject(ApiService);
  private overlayService = inject(GridOverlayService);
  private handler = inject(EffectHandlerService);


  getUser$ = createEffect(() => this.actions$.pipe(
    ofType(loadUser),
    switchMap(() => this.api.retrieveUser().pipe(
      this.handler.effectResponse({
        success: (user: User) => loadUserSuccess({ user }),
        retry: loadUser(),
        failure: loadUserFailure(),
        failureMessage: 'Could not retrieve user details',
      })
    ))
  ));

  getUserSettings$ = createEffect(() => this.actions$.pipe(
    ofType(loadUserSettings, ProjectActions.createSuccess),
    switchMap(() => this.api.retrieveUserSettings().pipe(
      this.handler.effectResponse({
        success: (userSettings: UserSettings) => loadUserSettingsSuccess({ userSettings }),
        retry: loadUserSettings(),
        failure: loadUserSettingsFailure(),
        failureMessage: 'Could not retrieve user settings',
      })
    ))
  ));

  updateUserSettings$ = createEffect(() => this.actions$.pipe(
    ofType(updateUserSettings),
    switchMap(({ settings }) => this.api.updateUserSettings(settings).pipe(
      this.handler.effectResponse({
        success: (userSettings: UserSettings) => updateUserSettingsSuccess({ userSettings }),
        retry: updateUserSettings({ settings }),
        failure: updateUserSettingsFailure(),
        failureMessage: 'Could not update user settings',
      })
    ))
  ));

  updateUserAccountSettings$ = createEffect(() => this.actions$.pipe(
    ofType(updateUserAccountSettings),
    concatLatestFrom(() => this.store.select(selectAccount)),
    filter((payload): payload is [ReturnType<typeof updateUserAccountSettings>, Account] => !!payload[1]),
    switchMap(([{ settings }, account]) => this.api.updateUserAccountSettings(account.id, settings).pipe(
      this.handler.effectResponse({
        success: (newSettings: UserAccountSettings) => updateUserAccountSettingsSuccess({ newSettings }),
        retry: updateUserAccountSettings({ settings }),
        failure: updateUserAccountSettingsFailure(),
        failureMessage: 'Could not update user account settings',
      })
    ))
  ));

  checkForUpdates$ = createEffect(() => this.actions$.pipe(
    ofType(checkForUpdates, getAccessTokenSuccess),
    switchMap(() => this.api.refreshUpdates().pipe(
      this.handler.effectResponse({
        success: (userSettings: UserSettings) => loadUserSettingsSuccess({ userSettings }),
        retry: checkForUpdates(),
        failure: loadUserSettingsFailure(),
        failureMessage: 'Could not retrieve updates',
      })
    ))
  ));

  checkForNotifications$ = createEffect(() => this.actions$.pipe(
    ofType(checkForNotifications, refreshAccessTokenSuccess),
    switchMap(() => this.api.refreshUpdates(true).pipe(
      this.handler.effectResponse({
        success: (userSettings: UserSettings) => loadUserSettingsSuccess({ userSettings }),
        retry: checkForNotifications(),
        failure: loadUserSettingsFailure(),
        failureMessage: 'Could not retrieve notifications',
      })
    ))
  ));

  deleteLatestUpdates$ = createEffect(() => this.actions$.pipe(
    ofType(deleteUpdates),
    switchMap(({ cases }) => this.api.deleteUpdates(cases).pipe(
      this.handler.effectResponse({
        success: (userSettings: UserSettings) => loadUserSettingsSuccess({ userSettings }),
        retry: deleteUpdates({ cases }),
        failure: loadUserSettingsFailure(),
        failureMessage: 'Could not delete updates',
      })
    ))
  ));

  // retrieveAccountAssemblies$ = createEffect(() => this.actions$.pipe(
  //   ofType(updateSelectedAccount),
  //   filter(({ loadAssemblies }) => !!loadAssemblies),
  //   map(() => AssemblyLibraryActions.retrieve()),
  // ));

  loadAccountSettings$ = createEffect(() => this.actions$.pipe(
    ofType(loadAccountSettings, updateSelectedAccount),
    switchMap(() => this.api.retrieveAccountSettings().pipe(
      this.handler.effectResponse({
        success: (accountSettings: AccountSettings) => loadAccountSettingsSuccess({ accountSettings }),
        retry: loadAccountSettings(),
        failure: loadAccountSettingsFailure(),
        failureMessage: 'Could not retrieve account settings',
      })
    ))
  ));

  updateAccountSettings$ = createEffect(() => this.actions$.pipe(
    ofType(updateAccountSettings),
    switchMap(({ settings }) => this.api.updateAccountSettings(settings).pipe(
      this.handler.effectResponse({
        success: (accountSettings: AccountSettings) => updateAccountSettingsSuccess({ accountSettings }),
        retry: updateAccountSettings({ settings }),
        failure: updateAccountSettingsFailure(),
        failureMessage: 'Could not update account settings',
      })
    ))
  ));

  updateSelectedProjectAndProgramme$ = createEffect(() => this.actions$.pipe(
    ofType(
      ProjectActions.selectSuccess,
      ProjectActions.deselect,
      ProgrammeActions.select,
      ProgrammeActions.deselect,
    ),
    concatLatestFrom(() => [
      this.store.select(selectCurrentProgrammeId),
      this.store.select(selectCurrentProjectId),
    ]),
    map(([, current_programme, current_project]) => updateUserAccountSettings({
      settings: {
        current_project,
        current_programme,
      }
    }))
  ));

  removeTaskNotification$ = createEffect(() => this.actions$.pipe(
    ofType(removeTaskNotification),
    switchMap(({ uuid }) => this.api.removeSingleTaskNotification(uuid).pipe(
      this.handler.effectResponse({
        success: (userSettings: UserSettings) => loadUserSettingsSuccess({ userSettings }),
        retry: removeTaskNotification({ uuid }),
        failure: loadUserSettingsFailure(),
        failureMessage: 'Could not remove task notification',
      })
    ))
  ));

  removeAllTaskNotification$ = createEffect(() => this.actions$.pipe(
    ofType(removeAllTaskNotifications),
    switchMap(() => this.api.removeAllTaskNotifications().pipe(
      this.handler.effectResponse({
        success: (userSettings: UserSettings) => loadUserSettingsSuccess({ userSettings }),
        retry: removeAllTaskNotifications(),
        failure: loadUserSettingsFailure(),
        failureMessage: 'Could not remove all task notifications',
      })
    ))
  ));

  getBalance$ = createEffect(() => this.actions$.pipe(
    ofType(getAccountBalance),
    switchMap(() => this.api.retrieveAccountBalance().pipe(
      this.handler.effectResponse({
        success: (balance) => getAccountBalanceSuccess({ balance }),
        retry: getAccountBalance(),
        failure: getAccountBalanceFailure(),
        failureMessage: 'Could not retrieve account balance',
      })
    ))
  ));

  createGridOverlay$ = createEffect(() => this.actions$.pipe(
    ofType(createGridOverlay),
    mergeMap(({ name, paths }) => this.api.createCustomOverlay(name, paths).pipe(
      this.handler.effectResponse({
        success: (overlay: CustomOverlay) => createGridOverlaySuccess({ overlay }),
        successMessage: 'Created Grid Overlay',
        retry: createGridOverlay({ name, paths }),
        failure: createGridOverlayFailure(),
        failureMessage: 'Could not create grid overlay',
      })
    ))
  ));

  updateGridOverlay$  = createEffect(() => this.actions$.pipe(
    ofType(updateGridOverlay),
    mergeMap(({ overlay }) => this.api.updateCustomOverlay(overlay).pipe(
      this.handler.effectResponse({
        success: (updatedOverlay: CustomOverlay) => updateGridOverlaySuccess({ overlay: updatedOverlay }),
        successMessage: 'Updated Grid Overlay',
        retry: updateGridOverlay({ overlay }),
        failure: updateGridOverlayFailure(),
        failureMessage: 'Could not update grid overlay',
      })
    ))
  ));

  deleteGridOverlay$ = createEffect(() => this.actions$.pipe(
    ofType(deleteGridOverlay),
    mergeMap(({ id }) => this.api.deleteCustomOverlay(id).pipe(
      this.handler.effectResponse({
        success: () => deleteGridOverlaySuccess({ id }),
        successMessage: 'Successfully Deleted Grid Overlay',
        retry: deleteGridOverlay({ id }),
        failure: deleteGridOverlayFailure(),
        failureMessage: 'Could not delete grid overlay',
      })
    ))
  ));

  ensureDefaultGridOverlay$ = createEffect(() => this.actions$.pipe(
    ofType(loadAccountSettingsSuccess),
    filter(({ accountSettings }) => !accountSettings.custom_overlays.length),
    map(() => createDefaultGridOverlay()),
  ));

  createDefaultGridOverlay$ = createEffect(() => this.actions$.pipe(
    ofType(createDefaultGridOverlay),
    map(() => createGridOverlay({
      name: 'Grid',
      paths: this.overlayService.generateDefaultPaths(),
    })),
  ));

  selectGridOverlayAfterCreateUpdate$ = createEffect(() => this.actions$.pipe(
    ofType(createGridOverlaySuccess, updateGridOverlaySuccess),
    map(({ overlay }) => selectGridOverlay({ gridOverlay: overlay.name }))
  ));

  createMapParameters$ = createEffect(() => this.actions$.pipe(
    ofType(createMapParameters),
    mergeMap(({ parameters }) => this.api.createMapParameters(parameters).pipe(
      this.handler.effectResponse({
        retry: createMapParameters({ parameters }),
        success: (map_parameters: AccountMapParameter[]) => createMapParametersSuccess({ map_parameters }),
        failure: createMapParametersFailure(),
        successMessage: 'Successfully Created Map Parameters',
        failureMessage: 'Could not create Map Parameters',
      })
    ))
  ));

  deleteMapParameter$ = createEffect(() => this.actions$.pipe(
    ofType(deleteMapParameter),
    mergeMap(({ key }) => this.api.deleteMapParameter(key).pipe(
      this.handler.effectResponse({
        retry: deleteMapParameter({ key }),
        success: () => deleteMapParameterSuccess({ key }),
        failure: deleteMapParameterFailure(),
        failureMessage: 'Could not delete Map Parameter',
      })
    ))
  ));

  updateMapParameter$ = createEffect(() => this.actions$.pipe(
    ofType(updateMapParameter),
    mergeMap(({ key, parameter }) => this.api.updateMapParameter(key, parameter).pipe(
      this.handler.effectResponse({
        retry: updateMapParameter({ key, parameter }),
        success: (_parameter: AccountMapParameter) => updateMapParameterSuccess({ parameter: _parameter }),
        failure: updateMapParameterFailure(),
        failureMessage: 'Could not update Map Parameter',
        successMessage: 'Successfully Updated Map Parameter',
      })
    ))
  ));
}
